/**
 * pages/okr.ts —— 团队 OKR（W2-5）
 * 来历：一期 [SEC:page-okr] 的 okrHtml / [SEC:page-okr-write] 的 O、KR 增删改与进度快调，
 *      渲染层重写、版式与交互不变。
 *
 * ## 与一期的四处差别（都不要"改回去"）
 * 1. 🔴 **进度快调是「滑块 + 数字框联动，松手才提交」**（2026-08-20 管理员变更 ④），
 *    一期是「数字框 + 保存按钮」。⚠ **拖动过程中一个请求都不发**——这条路径挂着乐观锁，
 *    拖一格发一次会让 `version` 冲突频繁误报。样式在 `styles/17_okr_progress.css`。
 * 2. 🔴 **有只读态**。一期固定管理员视角、页面上不出现只读；二期是全员真实登录：
 *    所有在用队伍的 OKR **全营可见**，但只有本队成员与管理员能改（基线 §5.3）。
 *    ⚠ 前端只是不显示按钮、把滑块禁掉——**拦截在服务端**（红线 4）。
 * 3. **一个数都不算**：团队进度、O 进度、风险条数、全营平均、逾期天数全部服务端下发。
 * 4. **版本冲突有专门的弹窗**：服务端整单拒绝并返回逐条前后值，前端**不自动覆盖也不自动合并**。
 *
 * ⚠ 已归档队伍不出现在本页（已退出本期考核），它的 OKR 在**队伍详情页**里只读可查。
 */
import type {
  KeyResultView,
  KrStatus,
  ObjectiveView,
  OkrBoardView,
  OkrGaps,
  OkrSortKey,
  OkrTeamDetail,
  OkrTeamRef,
  TeamPersonRef,
} from '@shared-types';
import { okr as okrApi } from '../api/index.js';
import { ApiClientError, errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, openModal, toast } from '../shared/common.js';
import { cut, esc, when } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { state } from '../shared/state.js';
import { refreshCurrent } from '../router/render.js';
import { definePage } from './types.js';

/** 状态四态。⚠ 逾期【不是】第五种状态，它是显示层派生值（RULES §一 OKR） */
const KRST: Record<KrStatus, { label: string; icon: string }> = {
  idle: { label: '未开始', icon: '' },
  doing: { label: '进行中', icon: '' },
  done: { label: '已完成', icon: IC.check },
  risk: { label: '有风险', icon: IC.warn },
};
const KRST_KEYS: readonly KrStatus[] = ['idle', 'doing', 'done', 'risk'];

/** 进度四档：同一色系靠明度区分，低 <60 / 中 60—79 / 高 80—99 / 完成 100（一期 lvOf 同款） */
export const lvOf = (p: number): string => (p >= 100 ? 'l4' : p >= 80 ? 'l3' : p >= 60 ? 'l2' : '');

const SORTS: ReadonlyArray<readonly [OkrSortKey, string, string]> = [
  ['id', '按编号', '按队伍编号升序'],
  ['okr', '按进度', '进度低的排前面，便于跟进'],
  ['risk', '按风险', '风险 KR 多的排前面'],
];

export interface OkrData {
  board: OkrBoardView;
}

/* ===================== 渲染（纯函数） ===================== */

const optHtml = (v: string, cur: string, lab: string): string =>
  `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(lab)}</option>`;

/** 左栏：队伍选择器。⚠ 排序已由服务端做好，这里不再排一次（红线 4） */
export function sideHtml(d: OkrData): string {
  const cur = d.board.team?.team.id ?? '';
  const sb = ([k, lab, tip]: readonly [OkrSortKey, string, string]): string =>
    `<button class="${state.ok.sort === k ? 'on' : ''}" data-okr="sort|${k}" title="${esc(tip)}">${lab}</button>`;

  const row = (t: OkrTeamRef): string => `
    <button class="okr-t ${t.id === cur ? 'on' : ''}" data-nav="#/okr/${esc(t.id)}"
      ${t.id === cur ? 'aria-current="true"' : ''} aria-label="${esc(t.name)}，进度 ${t.okr}%">
      <span class="okr-t1"><i class="num">${esc(t.code)}</i>${esc(t.name)}${
        t.risk ? `<em class="num" title="风险 KR ${t.risk} 条">${t.risk}</em>` : ''
      }</span>
      <span class="okr-t2"><span class="okrbar ${lvOf(t.okr)}"><i style="width:${t.okr}%"></i></span>
        <b class="num">${t.okr}%</b></span>
    </button>`;

  return `<div class="okr-sort"><b>排序</b>${SORTS.map(sb).join('')}</div>
    ${d.board.teams.map(row).join('')}`;
}

/**
 * 单条 KR。
 * ⚠ 快调区在只读态下是**禁用的滑块**而不是"什么都不显示"：别队的进度照样要看得见，
 *   看不见的只是"能拖"这件事。
 */
export function krRowHtml(k: KeyResultView, canEdit: boolean): string {
  const S = KRST[k.status] ?? KRST.doing;
  const due = k.due ? `截止 ${k.due.slice(5)}` : '未设截止日期';
  const own = k.ownerName ?? '未指定';
  /* ⚠ 2026-09-22 起改进度会自动带状态（拖满 100 → 已完成），这条提示只剩两种残留情形：
     ① 改动前就存着「100% 但未完成」的老数据；② 有人在编辑弹窗里显式把满进度的 KR 标成有风险。 */
  const tip = canEdit && k.progress >= 100 && k.status !== 'done';

  /* ⚠ 2026-09-15：名称可点开详情（全员可看）；描述只露一行；副行明确写「负责人」。
     「最近编辑人 · 日期」原是独立一列，和负责人并排显示成两个同名的名字、看不出含义，已移进详情弹窗。 */
  return `
  <div class="kr ${k.status === 'risk' ? 'r' : ''}" data-kr="${esc(k.id)}">
    <div class="krline">
      <div class="krn">
        <button type="button" class="krn-t" data-okr="krview|${esc(k.id)}" title="查看关键结果详情">KR${k.no}　${esc(k.title)}</button>
        ${when(!!k.desc, `<s class="krd" title="${esc(k.desc ?? '')}">${esc(k.desc ?? '')}</s>`)}
        <s>负责人 ${esc(own)} · ${esc(due)}${
          k.overdueDays ? ` <em>已逾期 ${k.overdueDays} 天</em>` : ''
        }</s>
      </div>
      <span class="krp">
        <!-- ⚠ 滑块【就是】那根进度条：一期的 .okrbar 在这一格，二期换成同尺寸的 range，
             只读态下 thumb 隐藏、拖不动，看起来仍是一根进度条。
             ⚠ 不要在旁边再放一根静态 .okrbar —— 两根同值的条只会让人以为它们是两个数。 -->
        <input type="range" class="krslide ${lvOf(k.progress)}" min="0" max="100" step="1"
          style="--p:${k.progress}%" value="${k.progress}" data-krs="${esc(k.id)}"
          ${canEdit ? '' : 'disabled'} aria-label="${esc(k.title)} 进度滑块（0—100，松手保存）">
        <b class="num" data-krv="${esc(k.id)}">${k.progress}%</b></span>
      <span class="krq">
        <input type="number" min="0" max="100" step="5" value="${k.progress}" data-krn="${esc(k.id)}"
          ${canEdit ? '' : 'disabled'} aria-label="${esc(k.title)} 进度（0—100）">
        <span class="unit">%</span>
      </span>
      <span class="krtag ${S.label === '进行中' ? 'doing' : k.status}">${S.icon}${S.label}</span>
      <span class="krops">${
        canEdit
          ? `<button class="tbtn" data-okr="kredit|${esc(k.id)}">编辑</button>
             <button class="tbtn danger" data-okr="krdel|${esc(k.id)}">删除</button>`
          : ''
      }</span>
    </div>
    ${
      tip
        ? `<div class="krtip"><button class="tbtn" data-okr="krdone|${esc(k.id)}">进度已满，可标记为已完成</button></div>`
        : ''
    }
  </div>`;
}

function objCardHtml(o: ObjectiveView, t: OkrTeamDetail): string {
  const canEdit = t.canEdit;
  return `
  <div class="objc" data-obj="${esc(o.id)}">
    <div class="objc-h">
      <i class="en">O${o.no}</i><b>${esc(o.title)}</b>
      <span class="objc-own">负责人 ${esc(o.ownerName ?? '未指定')}</span>
      <u class="num">${o.progress == null ? '—' : o.progress + '%'}</u>
      ${
        canEdit
          ? `<span class="objc-ops">
        <button class="tbtn" data-okr="oedit|${esc(o.id)}">编辑目标</button>
        <button class="tbtn" data-okr="krnew|${esc(o.id)}">＋ 新增关键结果</button>
        <button class="tbtn danger" data-okr="odel|${esc(o.id)}">删除目标</button>
      </span>`
          : ''
      }
    </div>
    ${when(!!o.desc, `<p class="objc-d">${esc(o.desc ?? '')}</p>`)}
    <div class="objc-b">${
      o.krs.length
        ? o.krs.map((k) => krRowHtml(k, canEdit)).join('')
        : `<div class="krempty"><span>${IC.info}</span>
           <span>该目标下暂无关键结果，进度显示为「—」，不参与团队平均</span>
           ${when(
             canEdit,
             `<button class="tbtn" data-okr="krnew|${esc(o.id)}" style="margin-left:auto">＋ 新增关键结果</button>`,
           )}</div>`
    }</div>
  </div>`;
}

/** 右栏：队伍头 + O 卡片 */
export function mainHtml(d: OkrData): string {
  const t = d.board.team;
  if (!t) {
    return emptyHtml(
      EIC.team,
      '当前没有生效的队伍',
      '已归档队伍的 OKR 仍可在队伍详情中查看',
      '<button class="tbtn" data-nav="#/teams">去队伍管理</button>',
    );
  }
  const team = t.team;
  // ⚠ owner_id 是 NOT NULL 外键：没有未停用的正式成员时建不出 O，入口给禁用态并说明原因
  const noOwner = t.ownerPool.length === 0;
  const newBtn = t.canEdit
    ? `<button class="btn primary" data-okr="onew" ${
        noOwner ? 'disabled title="该队暂无未停用的正式成员，请先在队伍管理里维护成员"' : ''
      }>${IC.plus}新增目标</button>`
    : '';

  const body = t.objectives.length
    ? t.objectives.map((o) => objCardHtml(o, t)).join('')
    : emptyHtml(
        EIC.flag,
        '该队尚未创建目标',
        /* ⚠ 2026-09-09 用户定：说明句去掉。「团队进度 = 全队 N 条 KR 等权平均」
           就在队伍头「团队进度」的悬浮说明里（2026-09-23 起不再平铺），同一屏说两遍。**不要加回来。** */
        '',
        t.canEdit
          ? `<button class="btn primary" data-okr="onew" ${noOwner ? 'disabled' : ''}>${IC.plus}新增目标</button>`
          : '',
      );

  /* 口径说明 2026-09-23 用户定：收进「团队进度」悬浮提示，不再平铺一行。
     说明行只剩会变的信息（风险 KR、只读），都没有就整行不出。 */
  const tip = `团队进度 = 全队 ${t.krCount} 条 KR 等权平均，不设权重与排名`;
  const notes: string[] = [];
  if (team.risk) notes.push(`当前有 <b class="num">${team.risk}</b> 条风险 KR`);
  if (!t.canEdit) notes.push('你不是本队成员，本页对你只读');

  return `
  <div class="okr-head">
    <div class="okr-hm">
      <h2><span class="no num">${esc(team.code)}</span>${esc(team.name)}</h2>
      <p><span class="topic" title="${esc(team.topic)}">${esc(team.topic)}</span> ｜ ${
        t.memberLine.length
          ? t.memberLine
              .map(
                (p) =>
                  `<span class="mname ${p.id === team.leadId ? 'lead' : ''}">${esc(p.name)}${
                    p.id === team.leadId ? '（队长）' : ''
                  }</span>`,
              )
              .join('')
          : '<span class="mname">该队暂无正式成员</span>'
      }</p>
    </div>
    <div class="okr-hr">
      <div class="okr-big" tabindex="0" aria-label="${esc(tip)}" data-tip="${esc(tip)}"><s>团队进度<i class="okr-q">?</i></s><b class="num">${team.okr}<small>%</small></b></div>
      ${newBtn}
    </div>
  </div>
  ${notes.length ? `<p class="okr-note">${notes.join(' · ')}</p>` : ''}
  ${gapHtml(t.gaps)}
  ${body}`;
}

/**
 * OKR 待完善提示（2026-09-23）。判定在服务端 `okrGaps()`，这里只翻成一句话。
 * ⚠ **全空时整行不出**——补齐就消失，不留一行「无」占位（与 v0.9.61 删「状态」列同一条理由：恒定的内容不提供信息）。
 * ⚠ 一条 O 都没有时也不出：下面的空态已经在说「尚未创建目标」，同一屏不说两遍。
 * ⚠ 中性灰，**不用 amber**——amber 在六色口径里专指风险 KR，这里不是风险（红线 1）。
 */
export function gapHtml(g: OkrGaps): string {
  if (g.noOkr) return '';
  const parts: string[] = [];
  if (g.objNoKr.length) parts.push(`${g.objNoKr.map((n) => `O${n}`).join('、')} 下还没有 KR`);
  if (g.krNoDue.length) parts.push(`<b class="num">${g.krNoDue.length}</b> 条 KR 未设截止日期`);
  if (!parts.length) return '';
  return `<p class="okr-gap">温馨提示：${parts.join('；')}，建议补充</p>`;
}

export function okrHtml(d: OkrData): string {
  const h = d.board.head;
  return `
  <div class="page okr-page">
    <div class="pagehead">
      <div>
        <h1>团队 OKR</h1>
        <p class="ph-sub" id="okr-sub"><span class="num">${h.teams}</span> 支队伍 ·
          平均进度 <span class="num">${h.avgOkr}%</span>（${h.okrTeams} 队算术平均）·
          风险 KR <span class="num">${h.riskKr}</span> 条</p>
      </div>
      <div class="ph-act">
        <span class="ph-note">进度拖动滑块或直接填数字，<b>松手即保存</b>。</span>
      </div>
    </div>
    <div class="okr-wrap" id="okr-wrap">
      <div class="okr-side" id="okr-side">${sideHtml(d)}</div>
      <div class="okr-main" id="okr-main">${mainHtml(d)}</div>
    </div>
  </div>`;
}

/* ===================== 写后的收尾 ===================== */

/** KR 行高亮 1.5 秒（一期 doKrFlash 同款）：渲染在路由之后，故先记待办，mounted 时消费 */
let pendingKr: string | null = null;
const flashKr = (id: string): void => {
  pendingKr = id;
};

/**
 * 版本冲突弹窗。
 * 🔴 RULES §二 4：**整单拒绝，不自动覆盖也不自动合并**——自动合并会让「谁的改动生效」变成运气。
 * ⚠ W2-6 双周报提交的冲突出参形状与这里**完全一致**（`ApiError.conflicts`），
 *   到时候 **import 这一个函数**，不要再写第二个冲突弹窗。
 */
export function krConflictModal(e: ApiClientError): void {
  const rows = (e.conflicts ?? [])
    .map(
      (c) => `<tr>
        <td>${esc(c.krTitle)}</td>
        <td class="num">${c.clientProgress}% · ${esc(labelOf(c.clientStatus))}</td>
        <td class="num"><b>${c.serverProgress}% · ${esc(labelOf(c.serverStatus))}</b></td>
      </tr>`,
    )
    .join('');
  openModal({
    title: '有人在你之前改过这条 KR',
    size: 'sm',
    body: `<p class="mtext">${esc(e.message)}</p>
      <table class="tbl" style="margin-top:12px">
        <thead><tr><th>关键结果</th><th>你填的</th><th>服务器上的</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="hint" style="margin-top:10px">系统不会替你合并两边的改动。看清最新值后再决定要不要重填。</p>`,
    footer: '<button class="btn primary" data-mclose>知道了，看最新值</button>',
    onClose: () => refreshCurrent(),
  });
}

const labelOf = (s: string): string => KRST[s as KrStatus]?.label ?? s;

/** 写操作后的统一收尾：原地刷新 + 轻提示。⚠ 不动 `meta` 缓存，理由见文件末 */
function afterWrite(msg: string, krId?: string): void {
  if (krId) flashKr(krId);
  refreshCurrent();
  toast(msg);
}

function onWriteError(e: unknown): void {
  if (e instanceof ApiClientError && e.code === 'VERSION_CONFLICT') return krConflictModal(e);
  toast(errText(e));
}

/* ===================== 进度快调（滑块 + 数字框，松手才提交） ===================== */

/** 正在提交的 KR：松手后到刷新回来之间再点一次会撞上乐观锁，直接忽略 */
const busy = new Set<string>();

const clamp = (n: number): number => (n < 0 ? 0 : n > 100 ? 100 : Math.round(n));

/**
 * 拖动 / 输入过程中的**本地联动**：数字框 ↔ 滑块 ↔ 进度条 ↔ 百分比，一个请求都不发。
 * ⚠ 这里直接改 DOM 是有意的：整页重渲染会打断拖动手势，也会丢滚动位置。
 */
function paintLive(id: string, value: number): void {
  const root = document.querySelector(`.kr[data-kr="${CSS.escape(id)}"]`);
  if (!root) return;
  const v = clamp(value);
  const slide = root.querySelector<HTMLInputElement>('[data-krs]');
  const num = root.querySelector<HTMLInputElement>('[data-krn]');
  if (slide) {
    slide.value = String(v);
    slide.style.setProperty('--p', v + '%');
    slide.className = `krslide ${lvOf(v)}`;
  }
  if (num && num.value !== String(v)) num.value = String(v);
  const txt = root.querySelector<HTMLElement>(`[data-krv]`);
  if (txt) txt.textContent = v + '%';
}

/**
 * 松手 / 失焦时提交。
 * ⚠ 值没变就不发请求：拖出去又拖回来、点一下滑块都会触发 change，
 *   每次都发一遍会把 `version` 白白 +1，别人的编辑页快照跟着失效。
 * ⚠ 状态原样带回：这个入口只提交进度，**状态由服务端按进度反推**（`normalizeKrWrite`，红线 4）——
 *   0 → 未开始、0<p<100 → 进行中、100 → 已完成，「有风险」在未满 100 时保留。前端不许自己算。
 */
function commitProgress(k: KeyResultView, raw: number): void {
  const v = clamp(raw);
  if (v === k.progress) return paintLive(k.id, k.progress);
  if (busy.has(k.id)) return;
  busy.add(k.id);
  document.querySelector(`.kr[data-kr="${CSS.escape(k.id)}"]`)?.classList.add('busy');

  void (async () => {
    try {
      // ⚠ version 是【进页那一版】：服务端比对不上会整单拒绝并返回逐条前后值
      const saved = await okrApi.saveProgress(k.id, { progress: v, status: k.status, version: k.version });
      // 服务端把状态带着改了就一并说出来，否则人会以为状态是自己手滑改的
      const st = saved.status !== k.status ? `，状态改为${KRST[saved.status]?.label ?? ''}` : '';
      afterWrite(`已更新「${cut(saved.title, 14)}」进度为 ${saved.progress}%${st}`, k.id);
    } catch (e) {
      onWriteError(e);
      // 失败时把界面退回服务端的值，别让人以为已经保存了
      paintLive(k.id, k.progress);
      document.querySelector(`.kr[data-kr="${CSS.escape(k.id)}"]`)?.classList.remove('busy');
    } finally {
      busy.delete(k.id);
    }
  })();
}

/** 「进度已满，可标记为已完成」：显式改状态的入口——服务端会把 progress 补齐 100 */
function markDone(k: KeyResultView): void {
  void (async () => {
    try {
      const saved = await okrApi.saveProgress(k.id, {
        progress: k.progress,
        status: 'done',
        version: k.version,
      });
      afterWrite(`已把「${cut(saved.title, 14)}」标记为已完成`, k.id);
    } catch (e) {
      onWriteError(e);
    }
  })();
}

/* ===================== 目标（O）弹窗 ===================== */

/* 字数上限：与服务端 `okr/service.ts` 的校验同值。⚠ 这里只管提示和 maxlength，
   真正的拦截仍在服务端（红线 4），改上限要两边一起改。 */
const O_TITLE_MAX = 30;
const O_DESC_MAX = 40;
const KR_TITLE_MAX = 40;
const KR_DESC_MAX = 100;

/** 输入框下方右侧的「n / 上限 字」；`left` 是同一行左侧的说明文字（可空） */
const cntHint = (id: string, len: number, max: number, left = ''): string =>
  `<div class="hint fld-cnt"><span>${left}</span><span data-cnt="${id}"${len >= max ? ' class="full"' : ''}><b class="num">${len}</b> / ${max} 字</span></div>`;

/** 输入时刷新计数；到上限时加 `.full`，让人知道「不是卡住了，是写满了」 */
function bindCounters(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('[data-cnt]').forEach((el) => {
    const input = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#${el.dataset.cnt}`);
    if (!input) return;
    input.addEventListener('input', () => {
      const n = input.value.length;
      const b = el.querySelector('b');
      if (b) b.textContent = String(n);
      el.classList.toggle('full', n >= input.maxLength);
    });
  });
}

const ownerOptions = (pool: TeamPersonRef[], cur: string): string =>
  pool.map((p) => optHtml(p.id, cur, `${p.name}（${p.dept}）`)).join('');

/** 新增 / 编辑目标（`o` 为 null 表示新增） */
function objModal(t: OkrTeamDetail, o: ObjectiveView | null): void {
  const pool = t.ownerPool;
  if (!pool.length) return void toast('该队暂无未停用的正式成员，请先在队伍管理里维护成员');

  const init = {
    title: o?.title ?? '',
    desc: o?.desc ?? '',
    // ⚠ 编辑时哪怕原负责人已停用也要留在选项里，否则一保存就把负责人换掉了（W2-4 的教训）
    owner: o?.ownerId ?? (pool[0]?.id ?? ''),
  };
  const extra =
    o && !pool.some((p) => p.id === o.ownerId)
      ? optHtml(o.ownerId, init.owner, `${o.ownerName ?? '原负责人'}（已停用）`)
      : '';

  const body = `
    <div class="fld" data-f="title"><label>目标（O）名称<span class="req">*</span></label>
      <input type="text" id="o-t" maxlength="${O_TITLE_MAX}" value="${esc(init.title)}" placeholder="如：跑通投研知识主链路">
      ${cntHint('o-t', init.title.length, O_TITLE_MAX)}
      <div class="err"></div></div>
    <div class="fld" data-f="desc"><label>说明</label>
      <input type="text" id="o-desc" maxlength="${O_DESC_MAX}" value="${esc(init.desc)}" placeholder="一句话说清这个目标要解决什么，可留空">
      ${cntHint('o-desc', init.desc.length, O_DESC_MAX, '留空时卡片上不显示说明行')}
      <div class="err"></div></div>
    <div class="fld" data-f="owner"><label>负责人<span class="req">*</span></label>
      <select id="o-own">${extra}${ownerOptions(pool, init.owner)}</select>
      <div class="hint">只能选本队正式成员</div>
      <div class="err"></div></div>`;

  const read = (root: ParentNode) => ({
    title: (root.querySelector<HTMLInputElement>('#o-t')?.value ?? '').trim(),
    desc: (root.querySelector<HTMLInputElement>('#o-desc')?.value ?? '').trim(),
    owner: root.querySelector<HTMLSelectElement>('#o-own')?.value ?? '',
  });

  const rec = openModal({
    title: o ? `编辑目标 · O${o.no}` : `新增目标（O）· ${t.team.code} ${t.team.name}`,
    body,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>${o ? '保存修改' : '创建目标'}</button>`,
    dirty() {
      const v = read(rec.mask);
      return v.title !== init.title || v.desc !== init.desc || v.owner !== init.owner;
    },
    onMount(root) {
      bindCounters(root);
      root.querySelector('[data-save]')?.addEventListener('click', () => submit(root));
      root.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
          submit(root);
        }
      });
    },
  });

  function submit(root: ParentNode): void {
    const v = read(root);
    /* ⚠ 前端只拦「明显没填」，**不是校验**：长度、负责人是不是本队正式成员一律由服务端判，
       文案直接用服务端返回的（红线 4）。 */
    if (!v.title) return mark(root, 'title', '请填写目标名称');
    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    void (async () => {
      try {
        const input = { title: v.title, desc: v.desc || null, ownerId: v.owner };
        const saved = o
          ? await okrApi.updateObjective(o.id, input)
          : await okrApi.createObjective({ ...input, teamId: t.team.id });
        closeModal(true);
        afterWrite(
          o
            ? `已更新目标「${cut(saved.title, 14)}」`
            : `已新增目标「${cut(saved.title, 14)}」，接着为它添加关键结果`,
        );
      } catch (e) {
        // ⚠ 错误留在弹窗里，不 toast：填的内容还在，人要在原地改
        mark(root, 'title', errText(e));
      } finally {
        if (btn) btn.disabled = false;
      }
    })();
  }
}

/* ===================== 关键结果（KR）弹窗 ===================== */

function krModal(t: OkrTeamDetail, o: ObjectiveView, k: KeyResultView | null): void {
  const pool = t.ownerPool;
  if (!pool.length) return void toast('该队暂无未停用的正式成员，请先在队伍管理里维护成员');

  const init = {
    title: k?.title ?? '',
    desc: k?.desc ?? '',
    owner: k?.ownerId ?? (pool[0]?.id ?? ''),
    due: k?.due ?? '',
    progress: String(k?.progress ?? 0),
    status: k?.status ?? ('idle' as KrStatus),
  };
  const extra =
    k && !pool.some((p) => p.id === k.ownerId)
      ? optHtml(k.ownerId, init.owner, `${k.ownerName ?? '原负责人'}（已停用）`)
      : '';

  const body = `
    <div class="fld-ro" style="margin-bottom:16px">所属目标：<b>O${o.no} ${esc(o.title)}</b></div>
    <div class="fld" data-f="title"><label>关键结果（KR）名称<span class="req">*</span></label>
      <input type="text" id="k-t" maxlength="${KR_TITLE_MAX}" value="${esc(init.title)}" placeholder="如：投研语料清洗与知识切片">
      ${cntHint('k-t', init.title.length, KR_TITLE_MAX)}
      <div class="err"></div></div>
    <div class="fld" data-f="desc"><label>描述</label>
      <textarea id="k-desc" class="okr-ta" rows="3" maxlength="${KR_DESC_MAX}"
        placeholder="说清这条 KR 具体要做到什么、怎么算完成，可留空">${esc(init.desc)}</textarea>
      ${cntHint('k-desc', init.desc.length, KR_DESC_MAX, '列表只显示一行，完整内容点 KR 名称查看')}
      <div class="err"></div></div>
    <div class="fld-2">
      <div class="fld" data-f="owner"><label>负责人<span class="req">*</span></label>
        <select id="k-own">${extra}${ownerOptions(pool, init.owner)}</select>
        <div class="err"></div></div>
      <div class="fld" data-f="due"><label>截止日期</label>
        <input type="date" id="k-due" value="${esc(init.due)}" data-clearable placeholder="未设截止日期">
        <div class="hint">可留空；已过期且未完成会显示逾期提醒</div>
        <div class="err"></div></div>
    </div>
    <div class="fld-2">
      <div class="fld" data-f="progress"><label>进度（%）<span class="req">*</span></label>
        <input type="number" id="k-p" min="0" max="100" step="5" value="${esc(init.progress)}">
        <div class="err"></div></div>
      <div class="fld" data-f="status"><label>状态<span class="req">*</span></label>
        <select id="k-s">${KRST_KEYS.map((x) => optHtml(x, init.status, KRST[x].label)).join('')}</select>
        <div class="hint">选「已完成」会自动把进度置为 100%</div>
        <div class="err"></div></div>
    </div>`;

  const read = (root: ParentNode) => ({
    title: (root.querySelector<HTMLInputElement>('#k-t')?.value ?? '').trim(),
    desc: (root.querySelector<HTMLTextAreaElement>('#k-desc')?.value ?? '').trim(),
    owner: root.querySelector<HTMLSelectElement>('#k-own')?.value ?? '',
    due: (root.querySelector<HTMLInputElement>('#k-due')?.value ?? '').trim(),
    progress: (root.querySelector<HTMLInputElement>('#k-p')?.value ?? '').trim(),
    status: (root.querySelector<HTMLSelectElement>('#k-s')?.value ?? 'idle') as KrStatus,
  });

  const rec = openModal({
    title: k ? `编辑关键结果 · KR${k.no}` : '新增关键结果（KR）',
    body,
    footer: `<button class="btn" data-mclose>取消</button>
            <button class="btn primary" data-save>${k ? '保存修改' : '创建 KR'}</button>`,
    dirty() {
      const v = read(rec.mask);
      return (
        v.title !== init.title ||
        v.desc !== init.desc ||
        v.owner !== init.owner ||
        v.due !== init.due ||
        v.progress !== init.progress ||
        v.status !== init.status
      );
    },
    onMount(root) {
      bindCounters(root);
      root.querySelector('[data-save]')?.addEventListener('click', () => submit(root));
      root.addEventListener('keydown', (e) => {
        const ev = e as KeyboardEvent;
        if (ev.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
          submit(root);
        }
      });
    },
  });

  function submit(root: ParentNode): void {
    const v = read(root);
    if (!v.title) return mark(root, 'title', '请填写关键结果名称');
    const p = Number(v.progress);
    if (v.progress === '' || !Number.isFinite(p)) return mark(root, 'progress', '请填写进度');

    const btn = root.querySelector<HTMLButtonElement>('[data-save]');
    if (btn) btn.disabled = true;
    void (async () => {
      try {
        /* ⚠ progress / status 都原样送，联动全部由**服务端** `normalizeKrWrite()` 定（唯一实现）：
           选 done → 补齐 100；只改了进度 → 状态按进度反推。
           前端抢着算会变成第二处实现，口径一改就只改得动一处。 */
        const input = {
          title: v.title,
          desc: v.desc || null,
          ownerId: v.owner,
          due: v.due || null,
          progress: Math.round(p),
          status: v.status,
        };
        const saved = k ? await okrApi.updateKr(k.id, input) : await okrApi.createKr(o.id, input);
        /* ⚠ 以服务端存下来的值为准报口径，不在前端预判：
           选 done → 进度被补齐 100；只改进度 → 状态被反推。两种都要说出来。 */
        const pSynced = saved.progress !== Math.round(p) ? `，已同步进度为 ${saved.progress}%` : '';
        const sSynced =
          saved.status !== v.status ? `，状态改为${KRST[saved.status]?.label ?? ''}` : '';
        closeModal(true);
        afterWrite(
          `${k ? '已更新' : '已新增'}关键结果「${cut(saved.title, 14)}」${pSynced}${sSynced}`,
          saved.id,
        );
      } catch (e) {
        mark(root, 'title', errText(e));
      } finally {
        if (btn) btn.disabled = false;
      }
    })();
  }
}

/* ===================== KR 详情（只读，全员可看） ===================== */

/**
 * KR 详情弹窗（2026-09-15）。列表里描述只露一行、「最近编辑」也不在列表上，完整信息都在这里。
 * ⚠ **只读**：进度 / 状态照服务端下发的显示，这里不放滑块——改进度仍在列表行快调，
 *   改其他字段点「编辑」进编辑弹窗。两个写入口已经够了（基线 §5.3），不再开第三个。
 * ⚠ 不展示 KR 历史：基线 §5.3「不提供完整 KR 历史界面，只显示最近编辑人与时间」。
 */
export function krDetailHtml(o: ObjectiveView, k: KeyResultView): string {
  const S = KRST[k.status] ?? KRST.doing;
  return `
    <div class="fld-ro">所属目标：<b>O${o.no} ${esc(o.title)}</b></div>
    <div class="krv-lab">描述</div>
    ${
      k.desc
        ? `<div class="krv-desc">${esc(k.desc)}</div>`
        : '<div class="krv-desc none">未填写描述</div>'
    }
    <div class="krv-grid">
      <div><s>进度</s><b class="num">${k.progress}%</b></div>
      <div><s>状态</s><b><span class="krtag ${S.label === '进行中' ? 'doing' : k.status}">${S.icon}${S.label}</span></b></div>
      <div><s>负责人</s><b>${esc(k.ownerName ?? '未指定')}</b></div>
      <div><s>截止日期</s><b>${esc(k.due ?? '未设置')}</b>${
        k.overdueDays ? `<em>已逾期 ${k.overdueDays} 天</em>` : ''
      }</div>
    </div>
    <div class="krv-last">最近更新：${esc(k.lastEditName)} · ${esc(k.lastEditAt.slice(0, 16))}</div>`;
}

function krDetailModal(t: OkrTeamDetail, o: ObjectiveView, k: KeyResultView): void {
  openModal({
    title: `KR${k.no} · ${cut(k.title, 24)}`,
    body: krDetailHtml(o, k),
    footer: `<button class="btn" data-mclose>关闭</button>${
      t.canEdit ? '<button class="btn primary" data-edit>编辑</button>' : ''
    }`,
    onMount(root) {
      root.querySelector('[data-edit]')?.addEventListener('click', () => {
        closeModal(true);
        krModal(t, o, k);
      });
    },
  });
}

/** 表单错误的统一落点（与打卡页同款：错误落在字段上，不是 toast） */
function mark(root: ParentNode, key: string, msg: string): void {
  root.querySelectorAll('.fld').forEach((f) => f.classList.remove('bad'));
  const f = root.querySelector(`[data-f="${key}"]`);
  if (!f) return void toast(msg);
  f.classList.add('bad');
  const err = f.querySelector('.err');
  if (err) err.textContent = msg;
  f.scrollIntoView({ block: 'center' });
}

/* ===================== 删除（均走二次确认，正文写清后果） ===================== */

function delObjective(o: ObjectiveView): void {
  confirmModal({
    title: '删除目标',
    okText: '删除目标',
    danger: true,
    html:
      `确认删除 <b>${esc(cut(o.title, 24))}</b>？<br>` +
      (o.krs.length
        ? `该 O 下的 <b>${o.krs.length}</b> 条 KR 将一并删除，团队进度会随之重算。`
        : '该目标下没有 KR，删除不影响团队进度。') +
      '<br>此操作不可撤销。',
    onOk: () => {
      void (async () => {
        try {
          await okrApi.deleteObjective(o.id);
          afterWrite(`已删除目标「${cut(o.title, 14)}」`);
        } catch (e) {
          onWriteError(e);
        }
      })();
    },
  });
}

function delKr(k: KeyResultView): void {
  confirmModal({
    title: '删除关键结果',
    okText: '删除关键结果',
    danger: true,
    html: `确认删除 <b>${esc(cut(k.title, 24))}</b>？<br>
      删除后该 KR 的进度不再计入团队平均，团队进度会随之变化，此操作不可撤销。`,
    onOk: () => {
      void (async () => {
        try {
          await okrApi.deleteKr(k.id);
          afterWrite(`已删除关键结果「${cut(k.title, 14)}」`);
        } catch (e) {
          onWriteError(e);
        }
      })();
    },
  });
}

/* ===================== 页面模块 ===================== */

/** 左栏滚动位置。⚠ `state.ok.scroll` 留给右栏（读的那一栏），两栏各存各的（一期同款） */
let sideScroll = 0;

const findKr = (d: OkrData, id: string): { o: ObjectiveView; k: KeyResultView } | null => {
  for (const o of d.board.team?.objectives ?? []) {
    const k = o.krs.find((x) => x.id === id);
    if (k) return { o, k };
  }
  return null;
};

export const okrPage = definePage<OkrData>({
  skeleton: 'detail',

  async load({ route, signal }) {
    // 队伍号始终在 hash 里（守卫已保证合法，并把默认队落成"进度最低的那支"）
    const teamId = route.view === 'okr' ? route.team : state.ok.team;
    const board = await okrApi.board(teamId, state.ok.sort as OkrSortKey, { signal });
    return { board };
  },

  html: (d) => okrHtml(d),

  mounted() {
    /* 两栏各自独立滚动，写操作后原地刷新要停在原来的位置——
       一期靠 refreshOkrPanel 只重渲染两栏，二期整页重渲染，所以在这里还原。 */
    const side = document.querySelector('#okr-side');
    const main = document.querySelector('#okr-main');
    if (side && sideScroll) side.scrollTop = sideScroll;
    if (main && state.ok.scroll) main.scrollTop = state.ok.scroll;

    if (!pendingKr) return;
    const id = pendingKr;
    pendingKr = null;
    const el = document.querySelector<HTMLElement>(`.kr[data-kr="${CSS.escape(id)}"]`);
    if (!el) return;
    el.classList.remove('flash');
    void el.offsetWidth;
    el.classList.add('flash');
    el.scrollIntoView({ block: 'nearest' });
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const team = d.board.team;

    /* ---- 进度快调：input 只做本地联动，change（= 松手 / 失焦）才提交 ---- */
    if (t instanceof HTMLInputElement && (t.dataset.krs || t.dataset.krn)) {
      const id = t.dataset.krs ?? (t.dataset.krn as string);
      if (e.type === 'input') return paintLive(id, Number(t.value));
      if (e.type === 'change') {
        const ref = findKr(d, id);
        if (ref) commitProgress(ref.k, Number(t.value));
        return;
      }
    }

    if (e.type !== 'click') return;

    /* 记一下当前滚动位置：写操作会整页重渲染，mounted 里靠它还原 */
    sideScroll = document.querySelector('#okr-side')?.scrollTop ?? sideScroll;
    state.ok.scroll = document.querySelector('#okr-main')?.scrollTop ?? state.ok.scroll;

    const op = t.closest<HTMLElement>('[data-okr]')?.dataset.okr;
    if (!op) return;
    const [kind, id] = op.split('|');

    if (kind === 'sort') {
      state.ok.sort = id as string;
      sideScroll = 0;
      return refreshCurrent();
    }
    if (!team) return;
    /* 查看详情是只读操作，全员可用，所以排在 canEdit 判定之前 */
    if (kind === 'krview' && id) {
      const r = findKr(d, id);
      return r ? krDetailModal(team, r.o, r.k) : undefined;
    }
    /* ⚠ 下面全是写操作：界面上不给按钮只是显示层，**拦截在服务端**（红线 4）。
       这里再判一次是为了防「按钮还在 DOM 里但数据已经变了」的竞态，不是权限本身。 */
    if (!team.canEdit) return;

    const obj = id ? team.objectives.find((o) => o.id === id) : null;
    const ref = id ? findKr(d, id) : null;

    if (kind === 'onew') return objModal(team, null);
    if (kind === 'oedit' && obj) return objModal(team, obj);
    if (kind === 'odel' && obj) return delObjective(obj);
    if (kind === 'krnew' && obj) return krModal(team, obj, null);
    if (kind === 'kredit' && ref) return krModal(team, ref.o, ref.k);
    if (kind === 'krdel' && ref) return delKr(ref.k);
    if (kind === 'krdone' && ref) return markDone(ref.k);
  },
});

/* ⚠ 写完 OKR **不调 `meta.refresh()`**（与队伍 / 周期的写操作不同）：
   OKR 不改变「有哪些队伍」「有哪些周期」，路由守卫的判定用不到它。
   `meta` 里缓存的 `Team.okr` 只有一个消费者——守卫挑「进度最低的那支」当默认队，
   而 hash 里始终带着队伍号，那条默认分支根本走不到。为它多打四个请求不值当。
   ⚠ 反过来说：哪天有人把 `meta.teams[].okr` 拿去渲染，就得在这里补上 refresh。 */
